import { useEffect, useRef, useState, type RefObject } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';
import { NavigationControl } from 'maplibre-gl';
import { Button } from '@ooh/ui/components/button';
import { MapView, type MapInstance, type MapPin } from '@ooh/ui/components/map-view';
import { MapLayerLegend, type LegendEntry } from '@ooh/ui/components/map-layer-legend';
import { REVIEW_MARKER_CATEGORY_COLORS } from './client-offer-review-marker-colors';
import type {
  ClientOfferReviewLayoutStep,
  ClientOfferReviewMapPanelLabels,
} from './client-offer-review-types';

/**
 * MapLibre pin map — click-to-focus (no popups, no clustering-in-spirit, no
 * hover-sync), legend, a real browser Fullscreen API toggle (top-right), and
 * a two-step list/map width chevron control (top-left, independent of the
 * top-right fullscreen/zoom cluster).
 */
export function ClientOfferReviewMapPanel({
  pins,
  categoryColors,
  selectedPinIds,
  onPinClick,
  mapInstanceRef,
  mapWrapRef,
  onToggleFullscreen,
  layoutStep,
  onLayoutStepChange,
  labels,
}: {
  pins: MapPin[];
  categoryColors: Record<string, string>;
  selectedPinIds: Set<string | number>;
  onPinClick: (id: string) => void;
  mapInstanceRef: RefObject<MapInstance | null>;
  mapWrapRef: RefObject<HTMLDivElement | null>;
  onToggleFullscreen: () => void;
  layoutStep: ClientOfferReviewLayoutStep;
  onLayoutStepChange: (step: ClientOfferReviewLayoutStep) => void;
  labels: ClientOfferReviewMapPanelLabels;
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const navAddedRef = useRef(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const canGrowList = layoutStep !== 'wideList';
  const canGrowMap = layoutStep !== 'wideMap';

  function growList() {
    if (layoutStep === 'wideMap') onLayoutStepChange('balanced');
    else if (layoutStep === 'balanced') onLayoutStepChange('wideList');
  }
  function growMap() {
    if (layoutStep === 'wideList') onLayoutStepChange('balanced');
    else if (layoutStep === 'balanced') onLayoutStepChange('wideMap');
  }

  // Colors come from REVIEW_MARKER_CATEGORY_COLORS — the same map that colors the
  // pins themselves (buildReviewMapPins) — so the legend can't drift from the pins
  // it explains (CU-86cbgnt2u). Mirrors MapLegend's pattern of deriving legend
  // entries from the pin-coloring map (components/map-legend.tsx).
  const legendEntries: LegendEntry[] = [
    { id: 'accepted', label: labels.legendAccepted, color: REVIEW_MARKER_CATEGORY_COLORS.accepted },
    { id: 'rejected', label: labels.legendRejected, color: REVIEW_MARKER_CATEGORY_COLORS.rejected },
    { id: 'digital', label: labels.legendDigital, color: REVIEW_MARKER_CATEGORY_COLORS.digital },
    { id: 'other', label: labels.legendOther, color: REVIEW_MARKER_CATEGORY_COLORS.other },
    { id: 'sold', label: labels.legendSold, color: REVIEW_MARKER_CATEGORY_COLORS.sold },
  ];

  return (
    <div
      ref={mapWrapRef}
      data-co-mapwrap
      data-testid="offer-review-map"
      // top and height both reference shared variables (apps/web/src/index.css)
      // so they can't drift apart. --co-map-height resolves here and accounts for
      // --co-map-header-offset, which AppShell's <main> (app-shell.tsx) sets to
      // account for the AppShell header on the internal version-preview and SSO
      // routes, and which falls back to 0 on the public offer route, which
      // renders without AppShell.
      className="sticky top-(--co-map-sticky-offset) h-(--co-map-height) min-h-(--co-map-min-height) overflow-hidden rounded-xl border border-border bg-muted"
    >
      <MapView
        pins={pins}
        categoryColors={categoryColors}
        selectedPinIds={selectedPinIds}
        onPinClick={(id) => onPinClick(String(id))}
        initialCenter={[52.1, 19.3]}
        initialZoom={5.3}
        clusterMaxZoom={4}
        clusterRadius={1}
        className="h-full w-full"
        onMapReady={(map) => {
          mapInstanceRef.current = map;
          if (!navAddedRef.current) {
            map.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');
            navAddedRef.current = true;
          }
        }}
      />
      <div className="absolute left-2.5 top-2.5 z-[5] flex items-center overflow-hidden rounded-lg border border-border bg-background/92">
        <Button
          type="button"
          variant="ghost"
          onClick={growList}
          disabled={!canGrowList}
          title={labels.growListWidth}
          aria-label={labels.growListWidth}
          data-testid="map-layout-grow-list"
          size="icon-md"
          className="rounded-none"
        >
          <ChevronRight className="h-4 w-4" strokeWidth={2} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={growMap}
          disabled={!canGrowMap}
          title={labels.growMapWidth}
          aria-label={labels.growMapWidth}
          data-testid="map-layout-grow-map"
          size="icon-md"
          className="rounded-none border-l border-border"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
        </Button>
      </div>
      <div className="absolute right-2.5 top-2.5 z-[5]">
        <Button
          type="button"
          variant="outline"
          onClick={() => onToggleFullscreen()}
          title={labels.fullscreenToggle}
          aria-label={labels.fullscreenToggle}
          data-testid="map-fullscreen-toggle"
          size="icon-md"
          className="rounded-lg border-border bg-background/92"
        >
          {isFullscreen ? (
            <Minimize2 className="h-4 w-4" strokeWidth={2} />
          ) : (
            <Maximize2 className="h-4 w-4" strokeWidth={2} />
          )}
        </Button>
      </div>
      <MapLayerLegend entries={legendEntries} />
    </div>
  );
}
